const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Splits a tags array or comma/semicolon-separated string into trimmed emails.
 * @param {string|string[]|null|undefined} value
 * @returns {string[]}
 */
export function splitEmailList(value) {
  let emails;
  if (Array.isArray(value)) {
    emails = value.map((email) => String(email || "").trim()).filter(Boolean);
  } else if (value == null || value === "") {
    return [];
  } else {
    emails = String(value)
      .split(/[,;\s]+/)
      .map((email) => email.trim())
      .filter(Boolean);
  }
  return emails;
}

/**
 * Splits a tags array or comma/semicolon-separated string into trimmed emails.
 * @param {string|string[]|null|undefined} value
 * @returns {string[]}
 */
export function parseEmailList(value) {
  const emails = splitEmailList(value);
  const seen = new Set();
  return emails.filter((email) => {
    const key = email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Joins emails for display in a single text field.
 * @param {string|string[]|null|undefined} value
 * @returns {string}
 */
export function formatEmailList(value) {
  return parseEmailList(value).join(", ");
}

/**
 * Validates a free-form email field that may contain multiple addresses.
 * @param {string|string[]|null|undefined} value
 * @param {{ requiredMessage?: string, invalidMessage?: string }} [options]
 * @returns {{ emails: string[], error: string }}
 */
export function validateEmailListField(value, options = {}) {
  const {
    requiredMessage = "At least one email address is required",
    invalidMessage = "Please enter valid email addresses",
    duplicateMessage = "Duplicate email address is not allowed.",
  } = options;
  const rawEmails = splitEmailList(value);
  if (!rawEmails.length) {
    return { emails: [], error: requiredMessage };
  }
  const seen = new Set();
  const hasDuplicate = rawEmails.some((email) => {
    const key = email.toLowerCase();
    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
  if (hasDuplicate) {
    return { emails: parseEmailList(value), error: duplicateMessage };
  }
  if (rawEmails.some((email) => !EMAIL_FORMAT.test(email))) {
    return { emails: rawEmails, error: invalidMessage };
  }
  return { emails: rawEmails, error: "" };
}

/**
 * Validates required fields in a data object.
 * @param {Object} data - The data object to validate.
 * @param {Array} requiredFields - Array of field definitions { key, label }.
 * @returns {Object} - An object containing error messages for missing fields.
 */
export function validateRequiredFields(data, requiredFields) {
  const errors = {};
  requiredFields.forEach((field) => {
    const value = data[field.key];
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === 1e-14 ||
      (Array.isArray(value) && value.length === 0)
    ) {
      const formattedLabel =
        field.label.charAt(0).toUpperCase() +
        field.label.slice(1).toLowerCase();
      errors[field.key] = `${formattedLabel} is required`;
    }
  });
  return errors;
}
